import { SchoolInfoType } from "@store/Schools";
import { useFormik } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import * as yup from "yup";
import Swal from "sweetalert2";
import { useHistory } from "react-router-dom";
import { AcademicTermType } from "@store/AcademicTerms";
import { PostalCodeType } from "@store/PostalCode";
import { StudentType } from "@store/Students";
import { AlertError } from "@utils/alert";

export interface AddInitialValueType {
  lastCharacter: string;
  firstName: string;
  lastName: string;
  dob: string;
  gender: string;
  school: string;
  academyYear: string;
  level: string;
  class: string | null;
  macAddress: string;
  startDate: string;
  toAndFrom: string;
  daySelect: number;
  isMonday: boolean;
  isTuesday: boolean;
  isWednesday: boolean;
  isThursday: boolean;
  isFriday: boolean;
  postalCode: string;
  unitNo: string;
  blockNo: string;
  buildingName: string;
  street: string;
  photoUrl: string | null;
  lat: string;
  lon: string;
  fullAddress: string;
  medicalCondition: string;
}

export interface EditInitialValue {
  lastCharacter: string;
  firstName: string;
  lastName: string;
  dob: string;
  gender: string;
  school: string;
  level: string;
  class: string | null;
  photoUrl: string | null;
  medicalCondition: string;
}

export const initialValues: AddInitialValueType = {
  lastCharacter: "",
  firstName: "",
  lastName: "",
  dob: "",
  gender: "",
  school: "",
  academyYear: "",
  level: "",
  class: "",
  macAddress: "",
  startDate: "",
  toAndFrom: "",
  daySelect: 0,
  isMonday: false,
  isTuesday: false,
  isWednesday: false,
  isThursday: false,
  isFriday: false,
  postalCode: "",
  unitNo: "",
  blockNo: "",
  buildingName: "",
  street: "",
  photoUrl: null,
  lat: "",
  lon: "",
  fullAddress: "",
  medicalCondition: "",
};

export const editInitialValues: EditInitialValue = {
  lastCharacter: "",
  firstName: "",
  lastName: "",
  dob: "",
  gender: "",
  school: "",
  level: "",
  class: "",
  photoUrl: null,
  medicalCondition: "",
};

interface useAddAndEditChildProp {
  token: string | null;
  selectSchool: SchoolInfoType | null;
  postCalculationStudent: (
    token: string,
    data: RegisterStudentType,
    callback: () => void
  ) => Promise<void>;
  id: string | undefined;
  updateStudent: (
    token: string,
    studentId: string,
    data: UpdateStudentType,
    callback: () => void
  ) => Promise<void>;
  userId: number | null;
  getListChilds: (
    token: string,
    userId: number,
    pageNumber: number
  ) => Promise<void>;
  academicTermsList: AcademicTermType[] | null;
  getAcademicYearsById: (
    token: string,
    academicYearId: number
  ) => Promise<void>;
  getListSchools: (token: string, search: string) => Promise<void>;
  setSelectSchool: (schoolName: string) => void;
  getAcademicYears: (token: string | null, SchoolId: number) => Promise<void>;
  getLevelSchools: (token: string | null, SchoolId: number) => Promise<void>;
  searchOneMap: (searchVal: string) => Promise<void>;
  searchPostalCode: (searchVal: string) => PostalCodeType | null;
  listPostalCodes: string[] | null;
  getDetailStudent: (
    token: string,
    studentId: string,
    callback: (x: StudentType) => void
  ) => Promise<void>;
  postAddStudent: (
    token: string,
    data: RegisterStudentType,
    callback: () => void
  ) => Promise<void>;
}

const useAddAndEditChild = ({
  token,
  selectSchool,
  postCalculationStudent,
  id,
  updateStudent,
  userId,
  getListChilds,
  academicTermsList,
  getAcademicYearsById,
  getListSchools,
  setSelectSchool,
  getAcademicYears,
  getLevelSchools,
  searchOneMap,
  searchPostalCode,
  listPostalCodes,
  getDetailStudent,
  postAddStudent,
}: useAddAndEditChildProp) => {
  const history = useHistory();

  const [isLoadingCost, setIsLoadingCost] = useState(false);

  const formikAdd = useFormik({
    initialValues,
    validationSchema: yup.object({
      lastCharacter: yup
        .string()
        .max(4, "Input 4 Characters")
        .min(4, "Input 4 Characters")
        .required("Last 4 characters + alphabet is required"),
      firstName: yup.string().required("First name is required"),
      lastName: yup.string().required("Last name is required"),
      dob: yup.string().required("Date Of Birth is required"),
      gender: yup.string().required("Gender is required"),
      school: yup.string().required("School is required"),
      academyYear: yup.string().required("Academic Year is required"),
      level: yup.string().required("level is required"),
      toAndFrom: yup.string().required("To And From  is required"),
      daySelect: yup
        .number()
        .moreThan(0, "Please Select Day")
        .required("Day is required"),
      street: yup.string().required("street is required"),
      lat: yup.string(),
      lon: yup.string(),
      class: yup.string().nullable().notRequired(),
      photoUrl: yup.string().nullable().notRequired(),
      startDate: yup.string().required(),
      medicalCondition: yup.string().notRequired(),
    }),
    onSubmit: (values) => {
      postCalculationStudent(
        token || "",
        {
          parentId: 0,
          schoolId: selectSchool?.id || 0,
          levelId: parseInt(values.level, 10),
          levelGroupId: values.class ? parseInt(values.class, 10) : null,
          firstName: values.firstName,
          lastName: values.lastName,
          gender: values.gender,
          macAddress: values.macAddress,
          dateOfBirth: values.dob,
          status: "active",
          studentSchedule: {
            academicYearId: parseInt(values.academyYear, 10),
            startedAt: values.startDate,
            isTaking: true,
            detail: {
              lat: values.lat,
              lng: values.lon,
              street: values.street,
              unitNumber: values.unitNo,
              blockNumber: values.blockNo,
              postalCode: values.postalCode,
              building: values.buildingName,
              isMonday: values.isMonday,
              isTuesday: values.isTuesday,
              isWednesday: values.isWednesday,
              isThursday: values.isThursday,
              isFriday: values.isFriday,
              isFromSchool:
                values.toAndFrom === "afternoon" || values.toAndFrom === "both",
              isToSchool:
                values.toAndFrom === "morning" || values.toAndFrom === "both",
            },
          },
          uniqueCode: values.lastCharacter,
          photoUrl: values.photoUrl ? values.photoUrl : null,
          medicalCondition: values.medicalCondition,
        },
        () => {}
      );
    },
  });

  const formikEdit = useFormik({
    initialValues: editInitialValues,
    validationSchema: yup.object({
      lastCharacter: yup
        .string()
        .max(4, "Input Max 4 Characters")
        .min(4, "Input 4 Characters")
        .required("Last 4 characters + alphabet of NRIC, FIN, Passport no."),
      firstName: yup.string().required("First name is required"),
      lastName: yup.string().required("Last name is required"),
      dob: yup.string().required("Date Of Birth is required"),
      gender: yup.string().required("Gender is required"),
      class: yup.string().nullable().notRequired(),
      photoUrl: yup.string().nullable().notRequired(),
      medicalCondition: yup.string().notRequired(),
    }),
    onSubmit: (values) => {
      if (id && token) {
        updateStudent(
          token,
          id,
          {
            id: parseInt(id, 10),
            levelId: parseInt(values.level),
            levelGroupId: values.class ? parseInt(values.class) : null,
            schoolId: parseInt(values.school),
            firstName: values.firstName,
            lastName: values.lastName,
            gender: values.gender,
            macAddress: "",
            dateOfBirth: values.dob,
            uniqueCode: values.lastCharacter,
            photoUrl: values.photoUrl || null,
            medicalCondition: values.medicalCondition || "",
          },
          () => {
            Swal.fire({
              icon: "success",
              title: "Update Successfully",
              text: "Student has been update",
              timer: 1500,
            }).then(() => {
              if (token && userId) {
                history.goBack();
                getListChilds(token, userId, 1);
              }
            });
          }
        );
      }
    },
  });

  useEffect(() => {
    /* Getting the academic year id from the academicTermsList. */
    if (academicTermsList && academicTermsList.length > 0 && token) {
      getAcademicYearsById(token, academicTermsList[0].academicYearId);
    }
  }, [academicTermsList]);

  /* This is a useEffect hook that is used to get the list of schools from the backend. */
  useEffect(() => {
    let timerFunc = setTimeout(() => {
      if (token && !id) {
        getListSchools(token, formikAdd.values.school);
        formikAdd.setValues({
          ...formikAdd.values,
          level: "",
          class: "",
          academyYear: "",
          startDate: "",
        });

        setSelectSchool(formikAdd.values.school);
      }
    }, 750);

    return () => clearTimeout(timerFunc);
  }, [formikAdd.values.school]);

  /* This is a useEffect hook that is used to get the list of academic years and levels from the backend. */
  useEffect(() => {
    let timerFunc = setTimeout(() => {
      if (selectSchool && token) {
        getAcademicYears(token, selectSchool.id);
        getLevelSchools(token, selectSchool.id);
      } else {
        getAcademicYears(token, 0);
        getLevelSchools(token, 0);
      }
    }, 500);
    return () => clearTimeout(timerFunc);
  }, [selectSchool]);

  // reset Start Date
  useEffect(() => {
    formikAdd.setValues({
      ...formikAdd.values,
      startDate: "",
    });
  }, [formikAdd.values.academyYear]);

  useEffect(() => {
    let timerFunc = setTimeout(() => {
      if (formikAdd.values.postalCode) {
        searchOneMap(formikAdd.values.postalCode);
      }
    }, 500);

    return () => clearTimeout(timerFunc);
  }, [formikAdd.values.postalCode]);

  useEffect(() => {
    const result = searchPostalCode(formikAdd.values.fullAddress);
    if (result) {
      formikAdd.setValues({
        ...formikAdd.values,
        unitNo: "",
        blockNo: result.BLK_NO,
        buildingName: result.BUILDING,
        street: result.ROAD_NAME,
        postalCode: result.POSTAL,
        lat: result.LATITUDE,
        lon: result.LONGITUDE,
      });
    }
  }, [
    formikAdd.values.postalCode,
    listPostalCodes,
    formikAdd.values.fullAddress,
  ]);

  useEffect(() => {
    if (id && token && userId) {
      getDetailStudent(token, id, (res) => {
        setTimeout(function () {
          if (res) {
            console.log(res);
            formikEdit.setValues({
              lastCharacter: res.uniqueCode || "",
              firstName: res.firstName || "",
              lastName: res.lastName || "",
              dob: res.dateOfBirth || "",
              gender: res.gender || "",
              school: res.schoolId.toString(),
              level: res.levelId.toString(),
              class: res.levelGroupId ? res.levelGroupId.toString() : null,
              photoUrl: res.photoUrl ? res.photoUrl : "",
              medicalCondition: res.medicalCondition || "",
            });
          }
        }, 500);
      });
    }
  }, [id]);

  /**
   * It checks if the latitude and longitude are empty, if they are, it searches for the postal code and
   * if it finds it, it sets the latitude and longitude to the values found
   * @returns A boolean value.
   */
  const handleSaveAdd = () => {
    const { values } = formikAdd;
    if (!values.lat && !values.lon) {
      const result = searchPostalCode(formikAdd.values.postalCode);

      if (
        result &&
        result.LATITUDE &&
        result.POSTAL &&
        result.LONGITUDE &&
        result.LATITUDE !== "NIL" &&
        result.LONGITUDE !== "NIL" &&
        result.POSTAL !== "NIL"
      ) {
        formikAdd.setValues({
          ...formikAdd.values,
          blockNo: result.BLK_NO,
          buildingName: result.BUILDING,
          street: result.ROAD_NAME,
          postalCode: result.POSTAL,
          lat: result.LATITUDE,
          lon: result.LONGITUDE,
        });
        return true;
      } else {
        AlertError(
          "Cannot Found postal code",
          "Please input valid postal code"
        );
        return false;
      }
    }
    if (values.postalCode === "NIL") {
      AlertError("Postal Code is not found", "Please input valid postal code");
      return false;
    }
    return true;
  };

  const actionCalculationCost = () => {
    if (token && handleSaveAdd()) {
      formikAdd.submitForm();
    }
  };

  const actionSave = useCallback(() => {
    if (token && userId && !id) {
      const { values } = formikAdd;

      postAddStudent(
        token,
        {
          parentId: userId,
          schoolId: selectSchool?.id || 0,
          levelId: parseInt(values.level, 10),
          levelGroupId: values.class ? parseInt(values.class, 10) : null,
          firstName: values.firstName,
          lastName: values.lastName,
          gender: values.gender,
          macAddress: values.macAddress,
          dateOfBirth: values.dob,
          status: "active",
          studentSchedule: {
            academicYearId: parseInt(values.academyYear, 10),
            startedAt: values.startDate,
            isTaking: true,
            detail: {
              lat: values.lat,
              lng: values.lon,
              street: values.street,
              unitNumber: values.unitNo,
              blockNumber: values.blockNo,
              postalCode: values.postalCode,
              building: values.buildingName,
              isMonday: values.isMonday,
              isTuesday: values.isTuesday,
              isWednesday: values.isWednesday,
              isThursday: values.isThursday,
              isFriday: values.isFriday,
              isFromSchool:
                values.toAndFrom === "afternoon" || values.toAndFrom === "both",
              isToSchool:
                values.toAndFrom === "morning" || values.toAndFrom === "both",
            },
          },
          uniqueCode: values.lastCharacter,
          photoUrl: values.photoUrl ? values.photoUrl : null,
          medicalCondition: values.medicalCondition || "",
        },
        () => {
          Swal.fire({
            icon: "success",
            title: "Add Child",
            text: "New child has been saved",
            timer: 1500,
          }).then(() => {
            if (token && userId) {
              history.goBack();
              getListChilds(token, userId, 1);
            }
          });
        }
      );
    }
  }, [formikAdd.values, token, userId]);

  return {
    formikAdd,
    formikEdit,
    actionSave,
    isLoadingCost,
    setIsLoadingCost,
    actionCalculationCost,
  };
};

export default useAddAndEditChild;
